/**
 * Le tunnel de commande : **quand** on écrit, et **où** on mène.
 *
 * Ces deux questions vivaient dans `CheckoutPage`, mêlées au rendu. Elles en
 * sortent parce qu'elles ont produit un défaut coûteux, et qu'un défaut de
 * séquence ne se voit pas en relisant du JSX.
 *
 * ## Le défaut du 10 septembre 2026
 *
 * Signalé après un essai réel :
 *
 *   « j'ai cliqué sur Valider les inscriptions, la boxe s'est ouverte […] et
 *     je suis resté là, n'ayant pas de croix pour sortir de cette boxe j'ai
 *     fermé l'onglet car je ne voulais plus valider cette inscription. Je
 *     reviens cet après-midi dessus et je vois que l'inscription a été validée
 *     quand même... »
 *
 * `create_order` commitait **avant** que la fenêtre s'ouvre, pour tenir la
 * place pendant le paiement. La libération, elle, reposait entièrement sur
 * `checkout.session.expired` — un événement Stripe. Or sans clic sur
 * Bancontact, **aucune session Stripe n'existe** : rien n'expire, donc rien
 * n'annule. La facture CDV-TEST-2602046 en est restée là, `pending`, sans PDF
 * et sans courriel, pour une inscription que personne n'avait voulue.
 *
 * L'ordre est donc inversé : **valider ouvre la fenêtre, et c'est le CHOIX qui
 * écrit**. Tant que le parent n'a pas choisi, la base ne sait rien de lui.
 *
 * Le prix accepté : entre l'ouverture de la fenêtre et le clic, la place n'est
 * pas retenue. Un refus peut donc arriver au clic plutôt qu'à la validation —
 * `create_order` le dit déjà en français (« la semaine du … n'a plus assez de
 * places »).
 *
 * ## Pourquoi ces règles sont ici et pas dans l'écran
 *
 * Même raison que `devis.ts` et `paiement/stripe.ts` : `gateway.supabase.ts`
 * importe le client Supabase, qui lève au chargement si les variables
 * d'environnement manquent. Une règle qu'on veut éprouver vit à côté du
 * transport, jamais dedans.
 */

import type { Commande } from './devis';

/**
 * L'adresse de la page de confirmation, pour une commande qui a commité.
 *
 * Elle est calculée **une fois**, au commit, et conservée : les deux issues du
 * paiement y ramènent, et le parent qui ferme la fenêtre y va aussi. Une seule
 * page, donc un seul endroit où « votre enfant est inscrit » est écrit.
 *
 * `communication` est la communication structurée : c'est elle qui part dans
 * le QR, et c'est elle que la banque du parent vérifie à la saisie.
 *
 * **Il n'y a pas de `?remaining=`.** L'adresse en portait un ; il valait le
 * reste dû AU MOMENT DE LA COMMANDE, donc il était faux dès qu'un paiement
 * passait — c'est le défaut de CDV-TEST-2602033, où le bandeau annonçait
 * « Paiement reçu » au-dessus d'un « Reste à payer : 3,00 € ». La page relit
 * `v_invoice_balances` et ne lit plus ce paramètre depuis ; le laisser dans
 * l'adresse ne ferait qu'inviter quelqu'un à le relire un jour.
 */
export function adresseDeConfirmation(commande: Commande): string {
  return (
    `/invoice-confirmation?invoiceNumber=${encodeURIComponent(commande.invoice_number)}`
    + `&invoiceId=${encodeURIComponent(commande.invoice_id)}`
    + `&communication=${encodeURIComponent(commande.communication ?? '')}`
    + `&registrationIds=${encodeURIComponent(JSON.stringify(commande.registration_ids ?? []))}`
  );
}

/** Ce qu'il faut faire pour honorer un choix de paiement. */
export type Ecriture =
  /** Rien n'a encore été écrit : c'est maintenant que `create_order` s'appelle. */
  | { forme: 'ecrire' }
  /** Une commande a déjà commité : on la reprend, on n'en crée pas une seconde. */
  | { forme: 'reprendre'; commande: Commande };

/**
 * Faut-il écrire la commande, ou reprendre celle qui existe déjà ?
 *
 * Le cas qui rend cette règle nécessaire : le parent clique sur Bancontact,
 * `create_order` commite, puis la création de la session Stripe échoue. Il
 * reste devant la fenêtre, **inscrit sans le savoir**, et il reclique.
 *
 * Sans cette règle, le second clic rappellerait `create_order` : l'index
 * partiel `registrations_une_inscription_active` refuserait la seconde
 * inscription, et le parent lirait « Cet enfant est déjà inscrit à cette
 * semaine » — une phrase exacte, et parfaitement incompréhensible pour lui,
 * puisque c'est lui-même qui vient de l'inscrire trois secondes plus tôt.
 *
 * On reprend donc la facture existante. Le second clic la mène chez Stripe,
 * ce que le premier n'avait pas réussi à faire.
 */
export function ecritureDuChoix(commande: Commande | null | undefined): Ecriture {
  return commande ? { forme: 'reprendre', commande } : { forme: 'ecrire' };
}

/** Ce que fermer la fenêtre de paiement doit produire. */
export type Sortie =
  /** Un aller-retour est en cours : fermer maintenant laisserait un écran menteur. */
  | { forme: 'attendre' }
  /** Rien n'a été écrit : on ferme, le panier est intact, la base n'a rien vu. */
  | { forme: 'abandonner' }
  /** La commande existe : on ne peut pas faire semblant, on mène à la confirmation. */
  | { forme: 'confirmer'; adresse: string };

/**
 * Que fait la croix, Échap, ou le clic sur le fond ?
 *
 * Trois issues, parce que trois situations différentes :
 *
 * - **En cours.** `create_order` ou la session Stripe est en vol. Fermer
 *   laisserait le parent devant son panier pendant qu'une facture s'écrit.
 *   On ne ferme pas ; les boutons sont déjà désactivés.
 *
 * - **Rien d'écrit.** Le cas ordinaire, et celui du 10 septembre : le parent
 *   se ravise avant de choisir. La fenêtre se ferme, il retrouve son panier,
 *   et **la base n'a jamais rien su de cette commande**.
 *
 * - **La commande existe.** Le parent a cliqué sur Bancontact, la commande a
 *   commité, et le paiement a échoué ou n'a pas pu s'ouvrir. Son enfant EST
 *   inscrit. Le renvoyer au panier lui ferait croire le contraire, et il
 *   recommencerait — on le mène donc à la confirmation, qui lui dit
 *   l'inscription acquise et le virement possible sous 15 jours.
 */
export function sortieDeLaFenetre(
  commande: Commande | null | undefined,
  enCours: boolean,
): Sortie {
  if (enCours) return { forme: 'attendre' };
  if (!commande) return { forme: 'abandonner' };
  return { forme: 'confirmer', adresse: adresseDeConfirmation(commande) };
}
